// guidesData.js
// Single source of truth for the Guides section.
// Both GuidesIndex (to list cards) and GuideArticle (to look up its own
// metadata for <title>/meta injection) read from this array.
//
// To add a guide:
//   1. Add an entry here (slug must match the article component + route).
//   2. Create the article component in ./articles/.
//   3. Register it in the ARTICLE_COMPONENTS map in GuideArticle.js.
//
// Slug rules: lowercase, hyphenated, IS the target keyword. Permanent once
// published (changing a live slug loses ranking unless you add a redirect).

export const GUIDES = [
  {
    slug: 'how-to-organize-a-weekly-golf-group',
    title: 'How to Organize a Weekly Golf Group: A Step-by-Step Playbook',
    // 150-160 char meta description, keyword near the front.
    metaDescription:
      'How to organize a weekly golf group step by step — from building your roster to sending tee times — and how automating the busywork saves organizers hours each week.',
    keyword: 'how to organize a weekly golf group',
    // Short excerpt shown on the index card.
    excerpt:
      'The complete playbook for running a recurring golf group — roster, RSVPs, pairings, reminders — and the case for letting software handle the parts that eat your week.',
    // ISO date. Used for Article schema datePublished; NOT shown prominently
    // (these are evergreen guides, not dated posts).
    datePublished: '2026-06-08',
    dateModified: '2026-06-08',
    readMinutes: 7,
    published: true,
  },
  {
    slug: 'how-to-run-a-golf-tournament-with-tee-time-organizer',
    title: 'Setting Up Your Tournament: Run a Golf Event From Start to Finish',
    metaDescription:
      'How to run a golf tournament in Tee Time Organizer from an empty account: add the course, create the event, bring in the field as guests, then follow your format guide to final results.',
    keyword: 'how to run a golf tournament',
    excerpt:
      'Bought an Event Pass? Start here: your course, your settings, the event, your golfers, then the guide for your format, printing, the live leaderboard and final results.',
    datePublished: '2026-09-30',
    dateModified: '2026-09-30',
    readMinutes: 6,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-captains-choice-golf-tournament',
    title: 'How to Set Up a Captain\u2019s Choice Golf Tournament: Step-by-Step Event Setup',
    metaDescription:
      'How to set up a captain\u2019s choice (scramble) golf tournament step by step: event setup, balanced teams, team handicaps, scorecards with QR codes, a live leaderboard, ties and final results.',
    keyword: 'how to set up a captains choice golf tournament',
    excerpt:
      'Every step of a captain\u2019s choice event in the order it has to happen: teams, team handicaps, team codes, printed cards, the live leaderboard, ties and final results.',
    datePublished: '2026-09-27',
    dateModified: '2026-09-27',
    readMinutes: 7,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-best-ball-golf-tournament',
    title: 'How to Set Up a Best Ball Golf Tournament: Step-by-Step Event Setup',
    metaDescription:
      'How to set up a best ball golf tournament step by step: 2-man, 3-man and 4-man formats, handicap allowances, teams, the blind draw for short teams, payouts, ties and live scoring.',
    keyword: 'how to set up a best ball golf tournament',
    excerpt:
      'Every step of a best ball event in the order it has to happen: teams, handicap allowances, the blind draw, printed cards, the live leaderboard, ties and payouts.',
    datePublished: '2026-09-27',
    dateModified: '2026-09-28',
    readMinutes: 8,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-stroke-play-golf-tournament',
    title: 'How to Set Up a Stroke Play Golf Tournament: Step-by-Step Event Setup',
    metaDescription:
      'How to set up a stroke play golf tournament step by step: net, gross or both, the handicap allowance, payouts and ties, scorecard QR codes, printed cards and the live leaderboard.',
    keyword: 'how to set up a stroke play golf tournament',
    excerpt:
      'Every step of a stroke play event in the order it has to happen: groups, the handicap allowance, payouts and ties, scorecard codes, printed cards and the live leaderboard.',
    datePublished: '2026-09-28',
    dateModified: '2026-09-28',
    readMinutes: 8,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-shamble-golf-tournament',
    title: 'How to Set Up a Shamble Golf Tournament: Step-by-Step Event Setup',
    metaDescription:
      'How to set up a shamble golf tournament step by step: 2-person and 4-person formats, the USGA handicap allowance, the drive rule, teams, the blind draw, printed cards, payouts, ties and live scoring.',
    keyword: 'how to set up a shamble golf tournament',
    excerpt:
      'Every step of a shamble event in the order it has to happen: teams, the handicap allowance, the drive rule, the blind draw, printed cards with a team row, the live leaderboard, ties and payouts.',
    datePublished: '2026-09-28',
    dateModified: '2026-09-28',
    readMinutes: 8,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-skins-game',
    title: 'How to Set Up a Skins Game: Step-by-Step for Your Golf Group',
    metaDescription:
      'How to set up a golf skins game for your group step by step: gross or net, carryover, no carryover or pot, strokes off the low man or full course handicap, who is in, and the settlement.',
    keyword: 'how to set up a skins game',
    excerpt:
      'Every step of a skins game for your weekly round: gross or net, carryover or pot, strokes off the low man or full handicap, who is in, following the skins and settling up.',
    datePublished: '2026-09-29',
    dateModified: '2026-10-04',
    readMinutes: 5,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-nassau-golf-game',
    title: 'How to Set Up a Nassau: Front, Back, Overall and Presses',
    metaDescription:
      'How to set up a Nassau golf game step by step: every player against every player or 2 vs 2 best ball, net or gross strokes, automatic or asked presses, and the settlement.',
    keyword: 'how to set up a nassau golf game',
    excerpt:
      'Every step of a Nassau for your weekly round: groups, matches, strokes, presses, who is in, the Press? prompt on the hole screen, results and settling up.',
    datePublished: '2026-10-04',
    dateModified: '2026-10-04',
    readMinutes: 6,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-match-play-golf-game',
    title: 'How to Set Up Match Play: Singles or Four-Ball',
    metaDescription:
      'How to set up a match play golf game step by step: singles in your group or 2 vs 2 four-ball, USGA strokes, full handicap or gross, 18 holes or a nine, and settling up.',
    keyword: 'how to set up a match play golf game',
    excerpt:
      'Every step of match play for your weekly round: singles or four-ball, the strokes, who is in, following the matches hole by hole, results and settling up.',
    datePublished: '2026-10-06',
    dateModified: '2026-10-06',
    readMinutes: 5,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-stableford-golf-game',
    title: 'How to Set Up Stableford: Points, Pot or Per Point',
    metaDescription:
      'How to set up a Stableford golf game step by step: within each group or the whole field, net or gross, a pot paid by places or so much a point, ties, live points and settling up.',
    keyword: 'how to set up a stableford golf game',
    excerpt:
      'Every step of a Stableford for your weekly round: within each group or the whole field, net or gross, the allowance, a pot (front, back and overall too) or so much a point, who is in, results and settling up.',
    datePublished: '2026-10-05',
    dateModified: '2026-10-07',
    readMinutes: 5,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-sixes-golf-game',
    title: 'How to Set Up Sixes: Three Six-Hole Matches, Partners by Cart',
    metaDescription:
      'How to set up a Sixes golf game step by step: three six-hole 2 vs 2 matches per foursome, partners set by cart and driver, net or gross, presses, and settling up.',
    keyword: 'how to set up a sixes golf game',
    excerpt:
      'Every step of Sixes for your weekly round: the foursomes, strokes and presses, the Carts card on the first tee, the match-over note at 6 and 12, results and settling up.',
    datePublished: '2026-10-05',
    dateModified: '2026-10-05',
    readMinutes: 6,
    published: true,
  },
  {
    slug: 'how-to-set-up-closest-to-the-pin',
    title: 'How to Set Up Closest to the Pin for Your Golf Group',
    metaDescription:
      'How to set up a closest to the pin game for your golf group: which par 3s, must make par, a prize per hole or so much a player, claiming the hole on your phone, and settling up.',
    keyword: 'how to set up closest to the pin golf game',
    excerpt:
      'Every step of closest to the pin for your weekly round: holes, must make par, the money, claiming a hole on the course, fixing a mis-tap and settling up.',
    datePublished: '2026-10-04',
    dateModified: '2026-10-04',
    readMinutes: 4,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-dots-golf-game',
    title: 'How to Set Up Dots: Birdies, Sandies and Polies',
    metaDescription:
      'How to set up a dots golf game for your group: birdies counted from the scores, sandies and polies tapped on the hole, individuals or teams, and settling the difference.',
    keyword: 'how to play dots golf game birdies sandies polies',
    excerpt:
      'Every step of a dots game: which dots count, individuals or teams, marking sandies and polies on the hole screen, fixing a dot and settling up.',
    datePublished: '2026-10-04',
    dateModified: '2026-10-04',
    readMinutes: 4,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-putts-golf-game',
    title: 'How to Set Up Putts Differential for Your Golf Group',
    metaDescription:
      'How to set up a putts game for your golf group: the wager per putt, entering putts on every hole, chip-ins, and how each pair of players settles the difference.',
    keyword: 'how to set up a putts game golf group',
    excerpt:
      'Every step of a putts game: the wager, who is in, entering putts on every hole, and how each pair settles the difference in total putts.',
    datePublished: '2026-10-04',
    dateModified: '2026-10-04',
    readMinutes: 4,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-stroke-play-game',
    title: 'How to Set Up a Stroke Play Game for Your Golf Group',
    metaDescription:
      'How to set up a weekly stroke play game for your golf group: net, gross or both, the handicap allowance, the buy-in, paid places, front and back nines, ties and settling up.',
    keyword: 'how to set up a stroke play game golf group',
    excerpt:
      'Every step of a weekly stroke play game: net or gross, the allowance, buy-in and paid places, front and back nines, who is in, ties and settling up.',
    datePublished: '2026-10-04',
    dateModified: '2026-10-04',
    readMinutes: 5,
    published: true,
  },
  {
    slug: 'how-to-set-up-a-best-ball-game',
    title: 'How to Set Up a Best Ball Game for Your Golf Group',
    metaDescription:
      'How to set up a weekly best ball game for your golf group: 2-man, 3-man or 4-man teams from your pairings, handicap allowances, the buy-in, paid places, ties and settling up.',
    keyword: 'how to set up a best ball game golf group',
    excerpt:
      'Every step of a weekly best ball game: teams from your pairings, 2-man, best 1 or 2 of 3, or best 1, 2 or 3 of 4, allowances, buy-in and places, and settling up.',
    datePublished: '2026-10-04',
    dateModified: '2026-10-04',
    readMinutes: 5,
    published: true,
  },
  {
    slug: 'getting-started-with-tee-time-organizer',
    title: 'Getting Started with Tee Time Organizer: Set Up Your Golf Group Step by Step',
    metaDescription:
      'How to set up your golf group in Tee Time Organizer step by step: courses and tees, players and handicaps, your first tee time, invitations, reminders and pairings.',
    keyword: 'how to set up a golf group organizer app',
    excerpt:
      'New to Tee Time Organizer? Start here: set up courses and players, create and publish your first tee time, send reminders and set pairings, in the order it has to happen.',
    datePublished: '2026-09-27',
    dateModified: '2026-09-27',
    readMinutes: 10,
    published: true,
  },
  {
    slug: 'scoring-your-golf-rounds',
    title: 'Scoring Your Golf Rounds: Live Scoring, Yardage, Leaderboards and Stats',
    metaDescription:
      'How your golf group can keep score on their phones: live hole-by-hole scoring, GPS yards to the green, a live leaderboard, GHIN posting, scorecards and detailed stats.',
    keyword: 'golf group live scoring app',
    excerpt:
      'Score on your phone hole by hole or after the round. TTO adds it all up, shows yards to the green and a live leaderboard, and builds every player\u2019s stats.',
    datePublished: '2026-09-27',
    dateModified: '2026-09-27',
    readMinutes: 9,
    published: true,
  },
  {
    slug: 'tee-time-organizer-player-guide',
    title: 'Player Guide: How to Use the Tee Time Organizer App',
    metaDescription:
      'A guide for golfers in a Tee Time Organizer group: opening the app, your profile and handicap, signing up for tee times, the waitlist, pairings, events, scoring on your phone and posting to GHIN.',
    keyword: 'golf group app player guide',
    excerpt:
      'Everything a player needs: open the app, set your handicap and tee, reserve a spot, see your group, play in events, and score and post to GHIN.',
    datePublished: '2026-09-28',
    dateModified: '2026-09-28',
    readMinutes: 8,
    published: true,
  },
  // --- Planned (not yet written). Set published:true when the component exists. ---
  {
    slug: 'running-a-golf-league-first-time',
    title: 'Running a Golf League for the First Time: What Most Organizers Miss',
    metaDescription:
      'A first-timer’s guide to running a golf league: setting up your group, handling handicaps and pairings, and the common mistakes that make the first season harder than it needs to be.',
    keyword: 'how to run a golf league for the first time',
    excerpt:
      'Taking on a league for the first time? Here’s what to set up before week one, and the avoidable mistakes that trip up most new organizers.',
    datePublished: '2026-06-08',
    dateModified: '2026-06-08',
    readMinutes: 8,
    published: false,
  },
  {
    slug: 'how-to-choose-golf-pairings',
    title: 'How to Choose Golf Pairings: Random, Balanced, or Fixed (and When Each Wins)',
    metaDescription:
      'How to choose golf pairings for your group: when random pairings beat balanced flights, when to balance groups by handicap for fair competition, and how to set up 2-, 3-, or 4-player groups.',
    keyword: 'how to choose golf pairings balanced flights vs random',
    excerpt:
      'Random, balanced by handicap, or fixed groups? The right pairing method depends entirely on what you are trying to accomplish. A decision guide for organizers.',
    datePublished: '2026-06-08',
    dateModified: '2026-06-08',
    readMinutes: 7,
    published: true,
  },
  {
    slug: 'ghin-handicaps-explained-for-leagues',
    title: 'GHIN Handicaps Explained for League Organizers',
    metaDescription:
      'How GHIN handicaps work for a golf league: what the Handicap Index is, how course handicaps are calculated with the WHS formula, and how to keep them current for your group.',
    keyword: 'how do GHIN handicaps work for a league',
    excerpt:
      'What every organizer should understand about GHIN, the Handicap Index, and how course handicaps are calculated for fair competition.',
    datePublished: '2026-06-08',
    dateModified: '2026-06-08',
    readMinutes: 7,
    published: false,
  },
  {
    slug: 'organizing-golf-group-email-vs-software',
    title: 'Organizing a Golf Group by Email vs Software: The Real Cost of the Manual Way',
    metaDescription:
      'Running your golf group on email, group texts, and a notepad? Here is what the manual method actually costs — in your time, in pace of play, and in your relationship with the club — and how software fixes it.',
    keyword: 'organize golf group without spreadsheets email group text',
    excerpt:
      'Email chains, reply-all texts, and a yellow notepad work — until they don’t. A clear-eyed look at what the manual method costs you, your players, and your club, and what changes with a real tool.',
    datePublished: '2026-06-08',
    dateModified: '2026-06-08',
    readMinutes: 9,
    published: true,
  },
];

// Helper: only guides that are actually live (have a component).
export const getPublishedGuides = () => GUIDES.filter((g) => g.published);

// Helper: look up a single guide by slug.
export const getGuideBySlug = (slug) => GUIDES.find((g) => g.slug === slug);

// Shared palette — copied verbatim from LandingPage.js so the Guides section
// is visually identical to the rest of the site. If the landing palette ever
// changes, update it here too (or lift it to a shared module).
export const colors = {
  green900: '#14532d', green800: '#166534', green700: '#15803d', green600: '#16a34a',
  green500: '#22c55e', green100: '#dcfce7', green50: '#f0fdf4',
  gray900: '#111827', gray800: '#1f2937', gray700: '#374151', gray600: '#4b5563',
  gray500: '#6b7280', gray400: '#9ca3af', gray300: '#d1d5db', gray200: '#e5e7eb',
  gray100: '#f3f4f6', gray50: '#f9fafb',
  amber500: '#f59e0b', amber400: '#fbbf24',
};
